/**
 * Audio Player with Chunk Separation Support
 * Handles timing metadata from the new worker implementation
 */
import { storage } from '../../utils/storage.js';
export class AudioPlayerWithSeparation {
    constructor(characterId, messageText) {
        this.characterId = characterId;
        this.messageText = messageText;
        this.audioContext = null;
        this.currentSource = null;
        this.isPlaying = false;
        this.chunkTimings = null;
        this.separationMs = 150;
        this.audioBuffer = null;
        this.decodedAudio = null;

        // Streaming support
        this.isStreaming = false;
        this.streamingAudio = null;
        this.streamStartTime = null;

        // Cancellation support to prevent overlapping playback
        this._cancelRequested = false;
        this._activeSources = []; // Track all active WebAudio sources for immediate stopping

        // Resume support - track playback position
        this._isPaused = false;
        this._currentChunkIndex = 0;
        this._chunkStartTime = 0;

        // NEW: Intra-chunk offset tracking
        this._chunkOffset = 0;
        this._chunkContextStartTime = 0;

        // For simple playback mode
        this._simpleOffset = 0;
        this._simpleContextStartTime = 0;

        // Add lock and debounce to prevent multiple instances
        this._playLock = false;
        this._lastClickTime = 0;
        this._debounceMs = 200;

        // Response timing for latency measurement
        this.responseReceivedTime = null;

        // Callback properties for external event handling
        this.onPlay = null;
        this.onEnded = null;
        this.onPause = null;
    }

    async initAudioContext() {
        if (!this.audioContext) {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }

        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }
    }

    /**
     * Prepare audio for playback in the background (preload and decode)
     */
    async prepareAudio(audioBuffer, chunkTimings, separationMs) {
        try {
            const prepareStart = performance.now();

            await this.initAudioContext();

            // Store parameters
            this.audioBuffer = audioBuffer;
            this.chunkTimings = chunkTimings;
            this.separationMs = separationMs;

            // Decode audio in background
            this.decodedAudio = await this.audioContext.decodeAudioData(audioBuffer.slice());

            this.isPrepared = true;
        } catch (error) {
            console.warn('🎵 Background audio preparation failed:', error);
            this.isPrepared = false;
        }
    }

    /**
     * Start streaming playback - begins audio immediately when streaming starts
     */
    async startStreamingPlayback(audioUrl, onStreamComplete = null) {
        try {
            this.isStreaming = true;
            this.streamStartTime = performance.now();

            // Start regular audio immediately for zero-latency feedback
            this.streamingAudio = new Audio(audioUrl);

            // Set up event listeners
            this.streamingAudio.addEventListener('play', () => {
                const latency = performance.now() - this.streamStartTime;
                if (this.onPlay) this.onPlay();
            });

            this.streamingAudio.addEventListener('ended', () => {
                this.isPlaying = false;
                this.isStreaming = false;
                if (this.onEnded) this.onEnded();
                if (onStreamComplete) onStreamComplete();
            });

            this.streamingAudio.addEventListener('error', (error) => {
                console.error('🎵 Streaming audio error:', error);
                this.isStreaming = false;
                this.isPlaying = false;
            });

            // Start playback
            this.isPlaying = true;
            await this.streamingAudio.play();

        } catch (error) {
            console.error('🎵 Failed to start streaming playback:', error);
            this.isStreaming = false;
            this.isPlaying = false;
            throw error;
        }
    }

    /**
     * Play audio with proper chunk separation using timing metadata
     */
    async playWithSeparation(audioBuffer, chunkTimings, separationMs = 500) {
        // Check if already locked to prevent multiple instances
        if (this._playLock) return;

        this._playLock = true;

        try {
            const startTime = performance.now();

            // Calculate total latency from response received to playback start
            if (this.responseReceivedTime) {
                const totalLatency = startTime - this.responseReceivedTime;
            }
            // Check if audio is already prepared in background
            if (this.isPrepared && this.decodedAudio && this.audioBuffer === audioBuffer) {
                // Update separation timing in case it changed
                this.separationMs = separationMs;
            } else {
                // Initialize audio context and start decoding in parallel
                const [_, decodedAudio] = await Promise.all([
                    this.initAudioContext(),
                    (async () => {
                        return await this.audioContext.decodeAudioData(audioBuffer.slice());
                    })()
                ]);

                this.audioBuffer = audioBuffer;
                this.chunkTimings = chunkTimings;
                this.separationMs = separationMs;
                this.decodedAudio = decodedAudio;
            }

            // Check if we should use chunk-by-chunk playback or simple playback
            const useChunkPlayback = chunkTimings && chunkTimings.length > 1 && separationMs > 0;

            const playbackStart = performance.now();

            // REMOVED: Frame-perfect resume logic - always start from beginning for replay
            // Reset any pause state to ensure clean playback from start
            this._isPaused = false;
            this._resumeSeconds = undefined;
            this._currentChunkIndex = 0;
            this._chunkOffset = 0;

            if (useChunkPlayback) {
                await this.playWithChunkSeparation();
            } else {
                await this.playSimple();
            }
        } catch (error) {
            console.error('Error playing audio with separation:', error);
            // Fallback to simple playback
            await this.playSimple();
        } finally {
            // Always release the lock when playback is done or fails
            this._playLock = false;
        }
    }

    /**
     * Play audio chunk by chunk with proper timing separation
     */
    async playWithChunkSeparation() {
        if (!this.chunkTimings || this.chunkTimings.length === 0) {
            await this.playSimple();
            return;
        }

        // Calculate actual chunk boundaries based on audio duration
        const totalDuration = this.decodedAudio.duration;
        const sampleRate = this.decodedAudio.sampleRate;

        // Reset or continue from paused position
        if (!this._isPaused) {
            this._currentChunkIndex = 0;
            this._chunkOffset = 0;
        }
        this._cancelRequested = false;
        this._isPaused = false;

        // CRITICAL: Set isPlaying to true at the start of chunk playback
        this.isPlaying = true;

        // Resume from the current chunk index
        for (let i = this._currentChunkIndex; i < this.chunkTimings.length; i++) {
            // Check cancellation before each chunk
            if (this._cancelRequested) {
                // Store current position for resume
                this._currentChunkIndex = i;
                this._isPaused = true;
                return; // Exit immediately to prevent further execution
            }

            const chunkTiming = this.chunkTimings[i];

            // Calculate chunk start and end times based on byte positions
            let startTime = (chunkTiming.startByte / this.audioBuffer.byteLength) * totalDuration;
            let endTime = (chunkTiming.endByte / this.audioBuffer.byteLength) * totalDuration;
            let chunkDuration = endTime - startTime;

            // Apply offset if resuming this chunk
            if (i === this._currentChunkIndex && this._chunkOffset > 0) {
                startTime += this._chunkOffset;
                chunkDuration -= this._chunkOffset;
                if (chunkDuration <= 0) {
                    // Skip to next chunk if offset exceeds duration
                    this._currentChunkIndex++;
                    this._chunkOffset = 0;
                    continue;
                }
            }

            // Skip chunks with zero or negative duration
            if (chunkDuration <= 0) {
                console.warn('🎵 Skipping chunk with invalid duration:', chunkDuration);
                continue;
            }

            // CRITICAL: Check cancellation again right before playing
            if (this._cancelRequested) {
                this._currentChunkIndex = i;
                this._isPaused = true;
                return;
            }

            try {
                // Play this chunk and wait for it to complete
                await this.playChunk(startTime, chunkDuration);
                // Reset offset after playing the resumed chunk
                this._chunkOffset = 0;
            } catch (error) {
                if (this._cancelRequested) {
                    // Chunk was cancelled - this is expected
                    this._currentChunkIndex = i;
                    this._isPaused = true;
                    return;
                } else {
                    // Actual error - rethrow
                    throw error;
                }
            }

            // Add separation delay if not the last chunk
            if (i < this.chunkTimings.length - 1) {
                // Check cancellation before delay
                if (this._cancelRequested) {
                    this._currentChunkIndex = i + 1; // Next chunk to play
                    this._isPaused = true;
                    return;
                }

                const actualSeparation = 500; // HARDCODED 500ms separation
                await this.delay(actualSeparation);

                // Check cancellation after delay
                if (this._cancelRequested) {
                    this._currentChunkIndex = i + 1; // Next chunk to play
                    this._isPaused = true;
                    return;
                }
            }
        }

        // Trigger ended event only if we completed all chunks
        if (!this._cancelRequested) {
            this._currentChunkIndex = 0; // Reset for next playback
            this._chunkOffset = 0;
            this._isPaused = false;
            this.isPlaying = false; // CRITICAL: Reset isPlaying when playback completes

            if (this.onEnded) {
                this.onEnded();
            }
        }
    }

    /**
     * Play a specific chunk of the audio
     */
    async playChunk(startTime, duration) {
        return new Promise((resolve, reject) => {
            try {
                const setupStart = performance.now();

                const source = this.audioContext.createBufferSource();
                source.buffer = this.decodedAudio;
                source.connect(this.audioContext.destination);

                // Track this source for immediate stopping on pause
                this._activeSources.push(source);
                this.currentSource = source;
                this.isPlaying = true;

                // Trigger play event for first chunk
                if (startTime === 0 && this.onPlay) {
                    this.onPlay();
                }

                source.onended = () => {
                    // Remove from active sources when it ends naturally
                    const index = this._activeSources.indexOf(source);
                    if (index > -1) {
                        this._activeSources.splice(index, 1);
                    }

                    // Clear current source reference but DON'T reset isPlaying
                    // The playWithChunkSeparation loop manages the overall playing state
                    if (this.currentSource === source) {
                        this.currentSource = null;
                    }
                    resolve();
                };

                source.addEventListener('error', (error) => {
                    // Remove from active sources on error
                    const index = this._activeSources.indexOf(source);
                    if (index > -1) {
                        this._activeSources.splice(index, 1);
                    }

                    this.isPlaying = false;
                    this.currentSource = null;
                    reject(error);
                });

                // Play the chunk with start time and duration
                // Record the exact context time when playback starts
                this._chunkContextStartTime = this.audioContext.currentTime;
                source.start(0, startTime, duration);
            } catch (error) {
                this.isPlaying = false;
                this.currentSource = null;
                reject(error);
            }
        });
    }

    /**
     * Delay utility for separation between chunks
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Play remaining audio starting at an exact offset (in seconds) – used for
     * frame-perfect resume after a pause.
     */
    async playFromPosition(positionSeconds = 0) {
        await this.initAudioContext();

        if (!this.decodedAudio) {
            if (!this.audioBuffer) throw new Error('No audio data for resume');
            this.decodedAudio = await this.audioContext.decodeAudioData(this.audioBuffer.slice());
        }

        const remaining = this.decodedAudio.duration - positionSeconds;
        if (remaining <= 0) {
            // CRITICAL: Reset state when there's nothing left to play
            this.isPlaying = false;
            this._isPaused = false;
            this._resumeSeconds = undefined;
            if (this.onEnded) this.onEnded();
            return;
        }

        const source = this.audioContext.createBufferSource();
        source.buffer = this.decodedAudio;
        source.connect(this.audioContext.destination);

        // Track this source for pause functionality
        this._activeSources.push(source);
        this.currentSource = source;
        this.isPlaying = true;

        source.onended = () => {
            // Remove from active sources when it ends naturally
            const index = this._activeSources.indexOf(source);
            if (index > -1) {
                this._activeSources.splice(index, 1);
            }

            // CRITICAL: Reset all resume state when playback completes
            this.isPlaying = false;
            this.currentSource = null;
            this._isPaused = false;
            this._resumeSeconds = undefined;
            this._currentChunkIndex = 0;
            this._chunkOffset = 0;

            if (this.onEnded) this.onEnded();
        };

        source.addEventListener('error', (error) => {
            // Remove from active sources on error
            const index = this._activeSources.indexOf(source);
            if (index > -1) {
                this._activeSources.splice(index, 1);
            }

            this.isPlaying = false;
            this.currentSource = null;
            this._isPaused = false;
            this._resumeSeconds = undefined;
        });

        // remember context start for pause offset calc
        this._chunkContextStartTime = this.audioContext.currentTime;
        source.start(0, positionSeconds, remaining);
    }

    async playSimple() {
        if (!this.decodedAudio) {
            if (this.audioBuffer) {
                this.decodedAudio = await this.audioContext.decodeAudioData(this.audioBuffer.slice());
            } else {
                throw new Error('No audio data available');
            }
        }

        const source = this.audioContext.createBufferSource();
        source.buffer = this.decodedAudio;
        source.connect(this.audioContext.destination);

        this.currentSource = source;
        this.isPlaying = true;

        // Trigger play callback to update UI
        if (this.onPlay) {
            this.onPlay();
        }

        source.onended = () => {
            this.isPlaying = false;
            this.currentSource = null;
            this._simpleOffset = 0;

            if (this.onEnded) {
                this.onEnded();
            }
        };

        // Apply offset if resuming
        let startOffset = 0;
        if (this._simpleOffset > 0) {
            startOffset = this._simpleOffset;
            const remainingDuration = this.decodedAudio.duration - startOffset;
            if (remainingDuration <= 0) {
                this._simpleOffset = 0;
                return;
            }
            this._chunkContextStartTime = this.audioContext.currentTime; // Reuse for simple mode
            source.start(0, startOffset, remainingDuration);
        } else {
            this._chunkContextStartTime = this.audioContext.currentTime; // Reuse for simple mode
            source.start(0);
        }
    }

    pause() {
        // Signal cancellation to chunk loop
        this._cancelRequested = true;

        // Stop ALL active WebAudio sources immediately (this is the key fix!)
        this._activeSources.forEach(source => {
            try {
                source.stop();
            } catch (e) {
                // Sources that already ended will throw an error, ignore
            }
        });
        this._activeSources = []; // Clear the array

        // Stop current source (redundant but safe)
        if (this.currentSource && this.isPlaying) {
            try { this.currentSource.stop(); } catch (e) { /* ignore */ }
            this.isPlaying = false;
            this.currentSource = null;
        }

        // Calculate offset using context time
        if (this._chunkContextStartTime > 0) {
            const elapsed = this.audioContext.currentTime - this._chunkContextStartTime;
            this._chunkOffset = elapsed;
            // Store absolute resume position (seconds from start of whole audio)
            if (this.chunkTimings && this.chunkTimings.length > 0 && this.decodedAudio) {
                const totalDuration = this.decodedAudio.duration;
                const currentChunkTiming = this.chunkTimings[this._currentChunkIndex] || null;
                if (currentChunkTiming) {
                    const chunkStart = (currentChunkTiming.startByte / this.audioBuffer.byteLength) * totalDuration;
                    this._resumeSeconds = chunkStart + this._chunkOffset;
                } else {
                    this._resumeSeconds = this._chunkOffset; // fallback
                }
            }
        }

        // For simple mode
        if (this._simpleContextStartTime > 0) {
            const elapsed = this.audioContext.currentTime - this._simpleContextStartTime;
            this._simpleOffset = elapsed;
        }

        // Stop streaming element if used
        if (this.streamingAudio && !this.streamingAudio.paused) {
            try { this.streamingAudio.pause(); } catch (e) { /* ignore */ }
            this.isStreaming = false;
        }

        // Do NOT release the play lock here – it will be cleared when
        // playWithSeparation finishes its cleanup. Releasing it early allows a
        // new playback to start while the previous playWithSeparation loop is
        // still unwinding, which causes overlapping audio.

        // CRITICAL: Reset isPlaying state when paused
        this.isPlaying = false;
        this._isPaused = true;

        // Trigger pause callback
        if (this.onPause) {
            this.onPause();
        }
    }

    stop() {
        this.pause();
    }
}

// Global audio preloader for faster playback
export const audioPreloader = {
    cache: new Map(),

    preload(url, key) {
        if (this.cache.has(key)) {
            return this.cache.get(key);
        }

        const promise = fetch(url, {
            priority: 'high',
            cache: 'force-cache'
        })
            .then(response => response.arrayBuffer())
            .then(buffer => {
                return buffer;
            })
            .catch(error => {
                console.error('🎵 Global preloader: Failed for', key, error);
                this.cache.delete(key); // Remove failed promise
                throw error;
            });

        this.cache.set(key, promise);
        return promise;
    },

    get(key) {
        return this.cache.get(key);
    },

    clear() {
        this.cache.clear();
    }
};

/**
 * Console commands for audio chunk separation control
 * Usage in browser console:
 * - setChunkSeparation(500) // Set 500ms separation
 * - getChunkSeparation()    // Get current setting
 * - toggleChunkSeparation() // Enable/disable feature
 */
window.setChunkSeparation = (ms) => {
    storage.set('chunkSeparationMs', ms.toString());
};

window.getChunkSeparation = () => {
    const current = parseInt(storage.get('chunkSeparationMs')) || 500;
    const enabled = storage.get('chunkSeparationEnabled') !== 'false';
    return { separationMs: current, enabled };
};

window.toggleChunkSeparation = () => {
    const current = storage.get('chunkSeparationEnabled') !== 'false';
    storage.set('chunkSeparationEnabled', (!current).toString());
};

window.setLatencyMode = (mode) => {
    // mode: 'instant' (prioritize speed), 'enhanced' (prioritize features)
    storage.set('audioLatencyMode', mode);

    if (mode === 'instant') {
        storage.set('chunkSeparationEnabled', 'false');
    } else if (mode === 'enhanced') {
        storage.set('chunkSeparationEnabled', 'true');
    }
};

window.setBufferTimeout = (ms) => {
    storage.set('audioBufferTimeout', ms.toString());
};

window.toggleVoiceStreaming = () => {
    const current = storage.get('useVoiceStreaming') !== 'false';
    storage.set('useVoiceStreaming', (!current).toString());
};

